import { Box, Button, Flex, Icon, Text, Textarea } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { VFC, useState } from "react";

import { track } from "lib/analytics";
import { ChatIcon, ThumbDownIcon, ThumbUpIcon } from "ui/icons";

type FeedbackState = "initial" | "thanked" | "feedback" | "submitted";

const ThumbButton: VFC<{
  icon: typeof ThumbUpIcon;
  label: string;
  onClick: () => void;
}> = ({ icon: IconComponent, label, onClick }) => (
  <Flex
    as="button"
    align="center"
    gap={1.5}
    bg="transparent"
    border="none"
    padding={1}
    borderRadius="md"
    color="text.tertiary"
    cursor="pointer"
    transition="color 150ms ease-out"
    outline="none"
    _hover={{ color: "text.primary" }}
    _focusVisible={{ boxShadow: "outline" }}
    onClick={onClick}
    aria-label={label}
  >
    <IconComponent boxSize="16px" flexShrink={0} />
    <Text fontSize="md" fontWeight="medium" lineHeight={1}>
      {label}
    </Text>
  </Flex>
);

export const Feedback: VFC = () => {
  const router = useRouter();
  const [state, setState] = useState<FeedbackState>("initial");
  const [feedbackText, setFeedbackText] = useState("");

  const handleSubmitFeedback = () => {
    if (feedbackText.trim()) {
      track("Feedback Text Submitted", {
        page: router.pathname,
        feedback_text: feedbackText.trim(),
      });
    }
    setState("submitted");
  };

  return (
    <Flex
      direction="column"
      fontSize="sm"
      color="text.secondary"
      mt={4}
      pt={4}
      borderTop="1px"
      borderColor="base.divider"
    >
      {(state === "thanked" || state === "submitted") && (
        <Flex align="center" gap={2}>
          <Icon
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            color="primary.base"
            boxSize="16px"
          >
            <path d="M20 6 9 17l-5-5" />
          </Icon>
          <Text fontSize="md" color="text.secondary">
            {state === "thanked"
              ? "Thanks for the feedback!"
              : "Feedback submitted — thank you!"}
          </Text>
        </Flex>
      )}

      {state === "initial" && (
        <Box>
          <Text fontSize="sm" fontWeight="medium" color="text.tertiary" mb={2}>
            Was this page helpful?
          </Text>
          <Flex align="center" gap={3}>
            <ThumbButton
              icon={ThumbUpIcon}
              label="Yes"
              onClick={() => {
                track("Page Helpful Icon Clicked", {
                  page: router.pathname,
                });
                setState("thanked");
              }}
            />
            <ThumbButton
              icon={ThumbDownIcon}
              label="No"
              onClick={() => {
                track("Page Not Helpful Icon Clicked", {
                  page: router.pathname,
                });
                setState("feedback");
              }}
            />
            <Box h="16px" w="1px" bg="base.divider" flexShrink={0} />
            <Flex
              as="button"
              align="center"
              gap={1.5}
              fontSize="md"
              fontWeight="medium"
              color="link.default"
              cursor="pointer"
              bg="transparent"
              border="none"
              padding={0}
              borderRadius="md"
              transition="color 150ms ease-out"
              outline="none"
              _hover={{ color: "link.hover" }}
              _focusVisible={{ boxShadow: "outline" }}
              onClick={() => setState("feedback")}
            >
              <ChatIcon boxSize="16px" flexShrink={0} />
              <Text as="span" lineHeight={1}>
                Send feedback
              </Text>
            </Flex>
          </Flex>
        </Box>
      )}

      {state === "feedback" && (
        <Flex direction="column" gap={3}>
          <Text fontSize="sm" fontWeight="medium" color="text.tertiary">
            What could be better?
          </Text>
          <Textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Missing info, unclear steps, outdated screenshots..."
            size="sm"
            fontSize="md"
            border="1px solid"
            borderColor="base.border"
            borderRadius="lg"
            rows={3}
            px={3}
            py={2.5}
            bg="white"
            _hover={{ borderColor: "gray.400" }}
            _focus={{
              borderColor: "primary.base",
              boxShadow: "outline",
            }}
            autoFocus
          />
          <Flex gap={2} align="center">
            <Button size="sm" onClick={handleSubmitFeedback}>
              Submit
            </Button>
            <Button
              size="sm"
              variant="subtle"
              onClick={() => {
                setFeedbackText("");
                setState("initial");
              }}
            >
              Cancel
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
